import React, { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  TextInput,
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  Button,
  Checkbox,
  Alert,
  ScrollArea,
  Loader,
  Center,
  Divider
} from '@mantine/core';
import {
  IconSearch,
  IconBuilding,
  IconAlertTriangle,
  IconArrowsShuffle
} from '@tabler/icons-react';
import { BusinessListItemDto } from '../../../../domain/entities/Business';
import { CompanyDto } from '../../../../domain/entities/Company';
import { useCompaniesByRegion, useBulkReassignBusinesses } from '../../../../application/queries/useCompanies';

interface ReassignBusinessesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBusinesses: BusinessListItemDto[];
  onReassignComplete: () => void;
}

/**
 * ReassignBusinessesModal - Modal for bulk reassigning businesses to a target company
 * Sprint 053: Admin business reassignment for consolidating chain locations.
 */
export const ReassignBusinessesModal: React.FC<ReassignBusinessesModalProps> = ({
  isOpen,
  onClose,
  selectedBusinesses,
  onReassignComplete
}): React.ReactElement => {
  const [companySearchQuery, setCompanySearchQuery] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(null);
  const [deleteOrphans, setDeleteOrphans] = useState<boolean>(true);
  const [errors, setErrors] = useState<string[]>([]);

  const reassignMutation = useBulkReassignBusinesses();

  // Validate all selected businesses share the same regionId
  const regionValidation = useMemo((): { valid: boolean; regionId: string | null; regionName: string | null } => {
    if (selectedBusinesses.length === 0) {
      return { valid: false, regionId: null, regionName: null };
    }
    const firstRegionId = selectedBusinesses[0]?.regionId;
    const firstRegionName = selectedBusinesses[0]?.regionName;
    if (!firstRegionId) {
      return { valid: false, regionId: null, regionName: null };
    }
    const allSameRegion = selectedBusinesses.every(b => b.regionId === firstRegionId);
    return {
      valid: allSameRegion,
      regionId: allSameRegion ? firstRegionId : null,
      regionName: allSameRegion ? (firstRegionName ?? null) : null
    };
  }, [selectedBusinesses]);

  // Company search query (min 2 chars, same region)
  const companySearchResults = useCompaniesByRegion(
    regionValidation.regionId ?? '',
    {
      searchTerm: companySearchQuery || undefined,
      page: 1,
      pageSize: 50
    },
    {
      enabled: isOpen && companySearchQuery.length >= 2 && regionValidation.valid
    }
  );

  const hasResults = companySearchResults.data && companySearchResults.data.companies.length > 0;
  const tooMany = selectedBusinesses.length > 500;

  const handleSelectCompany = useCallback((company: CompanyDto): void => {
    setSelectedCompany(prev => prev?.id === company.id ? null : company);
    setErrors([]);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setCompanySearchQuery(e.currentTarget.value);
    setSelectedCompany(null);
    setErrors([]);
  }, []);

  const handleClose = useCallback((): void => {
    setCompanySearchQuery('');
    setSelectedCompany(null);
    setDeleteOrphans(true);
    setErrors([]);
    onClose();
  }, [onClose]);

  const canReassign = selectedCompany !== null && regionValidation.valid && !tooMany && !reassignMutation.isPending;

  // Count how many distinct source companies will be affected
  const sourceCompanyCount = useMemo((): number => {
    const sourceCompanyIds = new Set<string>();
    for (const b of selectedBusinesses) {
      if (b.companyId && b.companyId !== selectedCompany?.id) {
        sourceCompanyIds.add(b.companyId);
      }
    }
    return sourceCompanyIds.size;
  }, [selectedBusinesses, selectedCompany]);

  const handleReassign = useCallback((): void => {
    if (!selectedCompany) return;

    setErrors([]);
    void reassignMutation.mutateAsync({
      targetCompanyId: selectedCompany.id,
      request: {
        businessIds: selectedBusinesses.map(b => b.id),
        deleteOrphanedCompanies: deleteOrphans
      }
    }).then((response) => {
      if (response.errors && response.errors.length > 0) {
        setErrors(response.errors);
      }
      if (response.success) {
        handleClose();
        onReassignComplete();
      }
    }).catch(() => {
      // Error toast already shown by ApiClient / mutation hook
    });
  }, [selectedCompany, selectedBusinesses, deleteOrphans, reassignMutation, handleClose, onReassignComplete]);

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Reassign Businesses to Company"
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Mixed region error */}
        {!regionValidation.valid && selectedBusinesses.length > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Mixed Regions">
            All selected businesses must be in the same region. Please adjust your selection.
          </Alert>
        )}

        {/* Too many selected warning */}
        {tooMany && (
          <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Too Many Selected">
            Maximum 500 businesses can be reassigned at once. You have {selectedBusinesses.length} selected.
          </Alert>
        )}

        {/* Selection summary */}
        <Group gap="xs">
          <Badge size="lg" variant="filled" color="blue">
            {selectedBusinesses.length} business{selectedBusinesses.length !== 1 ? 'es' : ''}
          </Badge>
          {regionValidation.regionName && (
            <Badge size="lg" variant="light" color="gray">
              {regionValidation.regionName}
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Company search */}
        <TextInput
          label="Search for target company"
          placeholder="Type at least 2 characters to search..."
          leftSection={<IconSearch size={16} />}
          value={companySearchQuery}
          onChange={handleSearchChange}
          disabled={!regionValidation.valid}
        />

        {/* Search results */}
        {companySearchQuery.length >= 2 && companySearchResults.isLoading && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}

        {companySearchQuery.length >= 2 && !companySearchResults.isLoading && !hasResults && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No companies found matching &ldquo;{companySearchQuery}&rdquo;
          </Text>
        )}

        {hasResults && (
          <ScrollArea h={250} type="auto">
            <Stack gap="xs">
              {companySearchResults.data?.companies.map((company) => (
                <Paper
                  key={company.id}
                  p="sm"
                  withBorder
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedCompany?.id === company.id ? '#e7f5ff' : 'transparent',
                    borderColor: selectedCompany?.id === company.id ? '#228be6' : undefined,
                    borderWidth: selectedCompany?.id === company.id ? 2 : 1
                  }}
                  onClick={(): void => handleSelectCompany(company)}
                >
                  <Group justify="space-between">
                    <Group gap="xs">
                      <IconBuilding size={16} color="var(--mantine-color-teal-6)" />
                      <Text fw={600} size="sm">{company.name}</Text>
                    </Group>
                    <Group gap="xs">
                      <Badge size="sm" variant="light" color="teal">
                        {company.businessCount} location{company.businessCount !== 1 ? 's' : ''}
                      </Badge>
                      {selectedCompany?.id === company.id && (
                        <Badge size="sm" variant="filled" color="blue">
                          Selected
                        </Badge>
                      )}
                    </Group>
                  </Group>
                  {company.description && (
                    <Text size="xs" c="dimmed" mt={4} lineClamp={1}>
                      {company.description}
                    </Text>
                  )}
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        )}

        <Divider />

        {/* Delete orphaned companies checkbox */}
        <Checkbox
          label="Delete orphaned companies (companies with no remaining businesses)"
          checked={deleteOrphans}
          onChange={(e): void => setDeleteOrphans(e.currentTarget.checked)}
        />

        {/* Error list from previous attempt */}
        {errors.length > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Errors">
            {errors.map((err, idx) => (
              <Text key={idx} size="sm">
                {err}
              </Text>
            ))}
          </Alert>
        )}

        {/* Summary */}
        {selectedCompany && (
          <Alert icon={<IconArrowsShuffle size={16} />} color="blue" variant="light">
            <Text size="sm">
              Reassign <Text component="span" fw={700}>{selectedBusinesses.length}</Text> business{selectedBusinesses.length !== 1 ? 'es' : ''} to{' '}
              <Text component="span" fw={700}>{selectedCompany.name}</Text>.
              {deleteOrphans && sourceCompanyCount > 0 && (
                <> Up to <Text component="span" fw={700}>{sourceCompanyCount}</Text> orphaned{' '}
                  compan{sourceCompanyCount !== 1 ? 'ies' : 'y'} may be deleted.</>
              )}
            </Text>
          </Alert>
        )}

        {/* Actions */}
        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconArrowsShuffle size={16} />}
            disabled={!canReassign}
            loading={reassignMutation.isPending}
            onClick={handleReassign}
          >
            Reassign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
